import React, { Fragment, FC } from 'react';
import {
	Orientation,
	Platform,
	DeviceType,
	Lightbox,
	PopOver,
	WriteDirection,
	PopOverSceneItemData,
	AppView,
	SceneHelperData,
} from 'sofa-reborn-react';
import { FooterWrapper, Div } from './ApplicationStyle';
import FooterContainer from '../../containers/FooterContainer';
import CoverPickerContainer from '../../containers/CoverPickerContainer';
import ArmrestPickerContainer from '../../containers/ArmrestPickerContainer';
import SummaryPageContainer from '../../containers/SummaryPageContainer';
import ProductIntroGalleryContainer from '../../containers/ProductIntroGalleryContainer';
import ConfirmCushionsContainer from '../../containers/ConfirmCushionsContainer';
import { KompisKeyboardInjector } from '@inter-ikea-kompis/react-components';
import PopoverContainer from '../../containers/PopoverContainer';
import PopoverMenuContainer from '../../containers/PopoverMenuContainer';
import ScreensaverContainer from '../../containers/ScreensaverContainer';
import TimeoutWarningContainer from '../../containers/TimeoutWarningContainer';
import AdditionalInformationContainer from '../../containers/AdditionalInformationContainer';
import BuildAreaContainer from '../../containers/BuildAreaContainer';
import MissingArticleContainer from '../../containers/MissingArticleContainer';
import LoadingSpinner from '../LoadingSpinner';
import SceneHelperContainer from '../../containers/SceneHelperContainer';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import FakeDataContainer from '../../containers/FakeDataContainer';
import NoValidLinkContainer from '../../containers/NoValidLinkContainer';
import RotationIconContainer from '../../containers/RotationIconContainer';
import { localeService } from '../../services';
import FailedPromptContainer from '../../containers/FailedPromptContainer';
import BuildTipContainer from '../../containers/BuildTipContainer';

type Props = {
	orientation: Orientation;
	platform: Platform;
	deviceType: DeviceType;
	writeDirection: WriteDirection;
	appView: AppView;
	currentLightbox: Lightbox;
	buildLimit: PopOver;
	disabledDrag: PopOver;
	middleModuleDrag: PopOver;
	unfinishedSection: PopOver;
	missingCoverSection: PopOver;
	invalidPlacement: PopOver;
	noValidConnection: PopOver;
	placementInformation: PopOver;
	selectedSceneItem: PopOverSceneItemData;
	actionButtonsVisible?: boolean;
	showLoadingSpinner?: boolean;
	showScreensaver?: boolean;
	showSelectAll?: boolean;
	sceneHelper?: SceneHelperData;
	showFakeDataForm?: boolean;
	features?: string[];
	missingCover: boolean;
	isInUpptackaPlatform: boolean;
	onInteraction?: () => void;
};

const Content: FC<Props> = (props) => (
	<Div
		orientation={props.orientation}
		platform={props.platform}
		onPointerDown={(): void => props.onInteraction && props.onInteraction()}
		onClick={(): void => props.onInteraction && props.onInteraction()}
		id="applicationWrapper"
	>
		{props.platform === 'kiosk' && props.showScreensaver ? <ScreensaverContainer /> : null}
		{props.platform === 'kiosk' ? <TimeoutWarningContainer /> : null}
		{!props.showLoadingSpinner && <ProductIntroGalleryContainer />}
		{props.currentLightbox === 'confirm-cushion' ? <ConfirmCushionsContainer /> : null}
		{props.currentLightbox === 'failed-prompt' ? <FailedPromptContainer /> : null}
		<BuildAreaContainer />
		{props.showSelectAll || props.selectedSceneItem ? <PopoverMenuContainer /> : null}
		{props.orientation === 'portrait' ? (
			<FooterWrapper id="footer">
				<FooterContainer />
			</FooterWrapper>
		) : null}
		<BuildTipContainer />
		{props.disabledDrag.showPopOver ? <PopoverContainer popOver={props.disabledDrag} /> : null}
		{props.buildLimit.showPopOver ? <PopoverContainer popOver={props.buildLimit} /> : null}
		{props.middleModuleDrag.showPopOver ? (
			<PopoverContainer popOver={props.middleModuleDrag} />
		) : null}
		{props.invalidPlacement.showPopOver ? (
			<PopoverContainer popOver={props.invalidPlacement} />
		) : null}
		{props.noValidConnection.showPopOver ? (
			<PopoverContainer popOver={props.noValidConnection} />
		) : null}
		{props.unfinishedSection.showPopOver ? (
			<PopoverContainer popOver={props.unfinishedSection} />
		) : null}
		{props.missingCoverSection.showPopOver ? (
			<PopoverContainer popOver={props.missingCoverSection} />
		) : null}
		{props.placementInformation.showPopOver ? (
			<PopoverContainer popOver={props.placementInformation} />
		) : null}
		{props.currentLightbox === 'cover-picker' ? <CoverPickerContainer /> : null}
		{props.currentLightbox === 'additional-cover-picker' ? (
			<CoverPickerContainer isAdditionalCover />
		) : null}
		{props.currentLightbox === 'armrest-picker' ? <ArmrestPickerContainer /> : null}
		{props.currentLightbox === 'additional-information' ? <AdditionalInformationContainer /> : null}
		{props.currentLightbox === 'no-valid-link' ? <NoValidLinkContainer /> : null}
		{props.currentLightbox === 'missing-article' ? <MissingArticleContainer /> : null}
		{!props.isInUpptackaPlatform ? <SummaryPageContainer /> : null}
		{props.showLoadingSpinner && (
			<LoadingSpinner platform={props.platform} color={'white'} isScreenshot={false} />
		)}
		{props.appView === 'build-view' ? <RotationIconContainer /> : null}
		{props.sceneHelper && <SceneHelperContainer />}
		{props.showFakeDataForm && <FakeDataContainer />}
	</Div>
);

const Application: FC<Props> = (props) => {
	return (
		<Fragment>
			{props.platform === 'kiosk' && !props.isInUpptackaPlatform ? (
				<KompisKeyboardInjector
					theme={SkapaTheme}
					layout={'qwerty'}
					doneButtonLabel={localeService.getTranslation('done')}
				>
					<Content {...props} />
				</KompisKeyboardInjector>
			) : (
				<Content {...props} />
			)}
		</Fragment>
	);
};

export default Application;
